import { type InjectionKey, type Ref, computed, inject, onUnmounted, provide, watch } from 'vue';

import { useRoute } from 'vue-router';

export type AutofillPolicy = 'allow' | 'block';

const AutofillPolicyKey: InjectionKey<Ref<AutofillPolicy>> = Symbol('autofillPolicy');

/**
 * Studio-facing UI (everything routed under /admin) blocks browser and
 * password-manager autofill: entry staff typing subject data must never get
 * suggestions from their personal contacts or from other studios' data.
 * Consumer-facing routes keep native autofill for checkout convenience.
 *
 * Call once from App.vue (requires router context). Returns the policy ref for
 * same-component use — provide() is not visible to inject() within the providing
 * component itself.
 */
export const provideAutofillPolicyFromRoute = (): Ref<AutofillPolicy> => {
  const route = useRoute();
  const policy = computed<AutofillPolicy>(() => (route.path.startsWith('/admin') ? 'block' : 'allow'));
  provide(AutofillPolicyKey, policy);
  return policy;
};

/** Override the route-derived policy for a component subtree. */
export const provideAutofillPolicy = (policy: Ref<AutofillPolicy>): void => {
  provide(AutofillPolicyKey, policy);
};

export const useAutofillPolicy = (): Ref<AutofillPolicy> =>
  inject(
    AutofillPolicyKey,
    computed(() => 'allow'),
  );

/**
 * Input attributes for the 'block' policy (empty object when allowed).
 * autocomplete=off stops the browser from remembering and re-suggesting typed
 * values (honored for plain text fields; only classified address/payment/password
 * fields ignore it). The data-* attributes are the documented opt-outs for
 * 1Password, LastPass, Bitwarden, and Dashlane respectively.
 */
export const useAutofillGuardAttrs = (): Ref<Record<string, string>> => {
  const policy = useAutofillPolicy();
  return computed(() => (policy.value === 'block' ? { autocomplete: 'off', ...VENDOR_OPT_OUT_ATTRS } : {}));
};

const VENDOR_OPT_OUT_ATTRS: Record<string, string> = {
  'data-1p-ignore': '',
  'data-lpignore': 'true',
  'data-bwignore': '',
  'data-form-type': 'other',
};

const GUARDED_SELECTOR = 'input, textarea, select, form';

const stampElement = (el: Element): void => {
  if (!el.matches(GUARDED_SELECTOR)) return;
  // Respect elements that declare their own autocomplete behavior.
  if (!el.hasAttribute('autocomplete')) el.setAttribute('autocomplete', 'off');
  if (el.tagName !== 'FORM') {
    Object.entries(VENDOR_OPT_OUT_ATTRS).forEach(([attr, value]) => {
      if (!el.hasAttribute(attr)) el.setAttribute(attr, value);
    });
  }
};

const stampTree = (root: Element | Document): void => {
  if (root instanceof Element) stampElement(root);
  root.querySelectorAll(GUARDED_SELECTOR).forEach(stampElement);
};

/**
 * Safety net for form fields rendered outside MInput/MForm (raw <input> elements
 * across studio pages): while the policy is 'block', a MutationObserver stamps the
 * guard attributes onto every form field as it enters the DOM. Call once from
 * App.vue with the ref returned by provideAutofillPolicyFromRoute.
 */
export const useAutofillDomGuard = (policy: Ref<AutofillPolicy>): void => {
  let observer: MutationObserver | null = null;

  const stop = () => {
    observer?.disconnect();
    observer = null;
  };

  watch(
    policy,
    (value) => {
      if (value === 'block' && !observer) {
        observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (node instanceof Element) stampTree(node);
            });
          });
        });
        observer.observe(document.body, { childList: true, subtree: true });
        stampTree(document);
      } else if (value !== 'block') {
        stop();
      }
    },
    { immediate: true },
  );

  onUnmounted(stop);
};
